// components/FileUploadSection.jsx
import React from 'react';
import { 
  Grid, Card, Typography, Box, Button, List, ListItem, ListItemIcon, 
  ListItemText, ListItemSecondaryAction, IconButton, LinearProgress,
  Alert
} from '@mui/material';
import { 
    AttachFile as AttachFileIcon, 
    CloudUpload as CloudUploadIcon,
    Upload as UploadIcon,
    AccountTree as FolderTreeIcon,
    Image as ImageIcon,
    PictureAsPdf as PictureAsPdfIcon,
    Description as DescriptionIcon,
    Delete as DeleteIcon,
    InsertDriveFile as FileIcon
  } from '@mui/icons-material';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const FileUploadSection = ({
  uploadedFiles = [],
  isFileUploading = false,
  createUploadPath,
  handleFileUpload,
  handleRemoveFile,
  setShowHierarchyDialog,
  selectedHierarchy = {},
  issueType,
  issueId = null,
  setIsFileUploading, // Added this prop
  error = null
}) => {
  
  const uploadPath = createUploadPath ? createUploadPath(selectedHierarchy, issueType) : 'default';
  
  const handleFileInputChange = async (event) => {
    try {
      // Check if the handleFileUpload function exists before calling it
      if (typeof handleFileUpload === 'function') {
        await handleFileUpload(event, selectedHierarchy, issueType, issueId);
      } else {
        //console.error('handleFileUpload function is not provided');
      }
    } catch (error) {
      //console.error('File upload failed:', error);
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType?.includes('image')) {
      return <ImageIcon />;
    } else if (fileType?.includes('pdf')) {
      return <PictureAsPdfIcon />;
    } else if (fileType?.includes('document') || fileType?.includes('text') || 
               fileType?.includes('word') || fileType?.includes('excel') ||
               fileType?.includes('powerpoint')) {
      return <DescriptionIcon />;
    } else {
      return <FileIcon />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const handleUploadClick = () => {
    if (!isFileUploading) {
      const fileInput = document.getElementById('general-file-upload');
      if (fileInput) {
        fileInput.click();
      }
    }
  };

  return (
    <Grid item xs={12}>
      <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          <AttachFileIcon sx={{ mr: 1 }} />
          Attachments
        </Typography>
        
        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {/* Upload Path Display */}
        <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Upload path: <code>{uploadPath}</code>
          </Typography>
        </Box>
        
        {/* File Upload Area */}
        <Box
          sx={{
            border: '2px dashed #ccc',
            borderRadius: 2,
            p: 3,
            textAlign: 'center',
            mb: 2,
            backgroundColor: '#f9f9f9',
            cursor: isFileUploading ? 'not-allowed' : 'pointer',
            opacity: isFileUploading ? 0.6 : 1,
            '&:hover': {
              backgroundColor: isFileUploading ? '#f9f9f9' : '#f0f0f0',
              borderColor: isFileUploading ? '#ccc' : '#999'
            }
          }}
          onClick={handleUploadClick}
        >
          <CloudUploadIcon sx={{ fontSize: 48, color: '#666', mb: 1 }} />
          <Typography variant="body1" sx={{ mb: 1 }}>
            {isFileUploading ? 'Uploading...' : 'Click to upload files or drag and drop'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Supports all file types (images, documents, PDFs, etc.)
          </Typography>
          
          <input
            accept="*/*"
            style={{ display: 'none' }}
            id="general-file-upload"
            multiple
            type="file"
            onChange={handleFileInputChange}
            disabled={isFileUploading}
          />
        </Box>

        {/* Alternative Upload Buttons */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={handleUploadClick}
            disabled={isFileUploading}
          >
            {isFileUploading ? 'Uploading...' : 'Choose Files'}
          </Button>
          
          {/* Uncomment if you want the hierarchy dialog */}
          {/* <Button
            variant="outlined"
            startIcon={<FolderTreeIcon />}
            onClick={() => setShowHierarchyDialog && setShowHierarchyDialog(true)}
            disabled={!selectedHierarchy?.project || isFileUploading}
          >
            Upload to Hierarchy
          </Button> */}
        </Box>

        {/* File Upload Progress */}
        {isFileUploading && (
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Uploading files...
            </Typography>
            <LinearProgress />
          </Box>
        )}

        {/* Uploaded Files List */}
        {uploadedFiles && uploadedFiles.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Uploaded Files ({uploadedFiles.length}):
            </Typography>
            <List dense>
              {uploadedFiles.map((file, index) => (
                <ListItem 
                  key={file.id || index} 
                  sx={{ 
                    px: 0,
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    mb: 1,
                    bgcolor: 'background.paper'
                  }}
                >
                  <ListItemIcon>
                    {getFileIcon(file.type)}
                  </ListItemIcon>
                  <ListItemText
                    primary={file.name}
                    secondary={
                      <Box>
                        <Typography variant="caption" display="block">
                          {formatFileSize(file.size)}
                        </Typography>
                        {file.hierarchyPath && (
                          <Typography variant="caption" color="text.secondary">
                            Path: {file.hierarchyPath}
                          </Typography>
                        )}
                        {file.uploadedAt && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Uploaded: {new Date(file.uploadedAt).toLocaleString()}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => handleRemoveFile && handleRemoveFile(file.id || index)}
                      size="small"
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {/* No Files Message */}
        {!uploadedFiles || uploadedFiles.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 2, color: 'text.secondary' }}>
            <Typography variant="body2">
              No files uploaded yet
            </Typography>
          </Box>
        ) : null}
      </Card>
    </Grid>
  );
};

export default FileUploadSection;